import type { AxiosInstance } from 'axios';
import { z } from 'zod';
import { type FilterData } from '@trinity/management-app-client';

export interface AgentDataSource {
  id: number;
  name: string;
}

export interface AgentFilter {
  id: number;
  identifier: string;
  label: string;
  type: number;
  operators: string[];
  data: FilterData;
}

export interface GetAgentDataSourcesRequest {
  agentId: number;
}

export interface GetAgentDataSourcesResponse {
  dataSources: AgentDataSource[];
}

export interface GetAgentFiltersRequest {
  agentId: number;
}

export interface GetAgentFiltersResponse {
  filters: AgentFilter[];
}

export interface GetAgentResourcesRequest {
  agentIdentifier: string;
}

export interface GetAgentResourcesResponse {
  dataSources: AgentDataSource[];
  filters: AgentFilter[];
}

export interface SearchAgentHistoryRequest {
  agentIdentifier: string;
  filterId: number;
  query?: string;
  brandIdentifier: string;
};

export interface SearchUserTerritoriesRequest {
  query?: string;
  brandIdentifier: string;
}

export interface GetUserTerritoriesRequest {
  q?: string;
  brandIdentifier: string;
}

type UserTerritory = {
  id: string;
  name: string;
  territoryKey: string;
  isDefault: boolean;
};

type GetUserTerritoriesResponse = {
  territories: UserTerritory[];
};

//Can't use Zod to infer this type since we aren't using strict nulls
type SearchAgentHistoryResponse = {
  results: { id: string | number; name: string }[];
};

const searchAgentHistoryResponseSchema = z.object({
  results: z.array(z.object({
    id: z.union([z.string(), z.number()]),
    name: z.string(),
  })),
});

const getUserTerritoriesResponseSchema = z.object({
  territories: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      territoryKey: z.string(),
      isDefault: z.boolean(),
    })
  ),
});

/** Runtime validation via Zod — returns unknown to force explicit typing at call site */
function validateAgentHistory(data: unknown): unknown {
  return searchAgentHistoryResponseSchema.parse(data);
}

function validateUserTerritories(data: unknown): unknown {
  return getUserTerritoriesResponseSchema.parse(data);
}

export class AgentResourcesClient {

  constructor(private authenticatedClient: AxiosInstance) { }

  getAgentResources = async (data: GetAgentResourcesRequest): Promise<GetAgentResourcesResponse> => {
    const response = await this.authenticatedClient.get<GetAgentResourcesResponse>(
      `/chat/agents/${data.agentIdentifier}/resources`,
    );
    return response.data;
  };

  searchAgentFilter = async (data: SearchAgentHistoryRequest): Promise<SearchAgentHistoryResponse> => {
    const response = await this.authenticatedClient.get(
      `/chat/agents/${data.agentIdentifier}/filter-search/${data.filterId}?query=${encodeURIComponent(data.query ?? '')}&brandIdentifier=${encodeURIComponent(data.brandIdentifier)}`
    );
    return validateAgentHistory(response.data) as SearchAgentHistoryResponse;
  };

  searchUserTerritories = async (data: SearchUserTerritoriesRequest): Promise<SearchAgentHistoryResponse> => {
    const response = await this.authenticatedClient.get(
      `/chat/users/territories/search?query=${encodeURIComponent(data.query ?? '')}&brandIdentifier=${encodeURIComponent(data.brandIdentifier)}`
    );
    return validateAgentHistory(response.data) as SearchAgentHistoryResponse;
  };

  getUserTerritories = async (data: GetUserTerritoriesRequest): Promise<GetUserTerritoriesResponse> => {
    const query = data.q
      ? `?q=${encodeURIComponent(data.q)}&brandIdentifier=${encodeURIComponent(data.brandIdentifier)}`
      : `?brandIdentifier=${encodeURIComponent(data.brandIdentifier)}`;
    const response = await this.authenticatedClient.get(`/chat/users/territories${query}`);
    return validateUserTerritories(response.data) as GetUserTerritoriesResponse;
  };
}
